import { computed, makeObservable } from 'mobx';

import { toError } from 'helpers/toError';
import { MobxQuery, type QueryValue } from 'mobx/MobxQuery';
import type { Received } from 'validation';

/**
 * What one read of the record came back with: the endpoint's answer, or the failed
 * request itself. A failure is recorded as an answer, not thrown, for the reason in
 * RecordDetailsModel.
 */
export type RecordRead<T> = Received<T | null> | { readonly result: 'failed'; readonly error: Error };

/**
 * What a details pane can show for its record.
 *
 * A degraded answer is shown, marked `incomplete`: the pane only displays it, and an
 * object the user can read most of is better than an error. The edit dialog reads the
 * record again and refuses it, so nothing is saved from it.
 *
 * `refreshing` / `retrying` mean a re-read is in flight. Only `loaded` shows a record
 * meanwhile: after a failure or a not-found there is nothing the pane can vouch for
 * until the new answer lands.
 */
export type DetailsState<T> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'not-found'; readonly refreshing: boolean }
  | { readonly kind: 'failed'; readonly error: Error; readonly retrying: boolean }
  | { readonly kind: 'loaded'; readonly record: T; readonly incomplete: boolean; readonly refreshing: boolean };

/** The state a by-id read's query value puts a details pane in. */
export const toDetailsState = <T>(value: QueryValue<RecordRead<T>>): DetailsState<T> => {
  switch (value.kind) {
    case 'uninitialized':
    case 'loading':
      return { kind: 'loading' };
    case 'error':
      return { kind: 'failed', error: value.error, retrying: false };
    case 'resolved':
    case 'stale': {
      const answer = value.kind === 'resolved' ? value.answer.value : value.previous.value;
      const inFlight = value.kind === 'stale';
      if (answer.result === 'failed') return { kind: 'failed', error: answer.error, retrying: inFlight };
      if (answer.result === 'unreadable') return { kind: 'failed', error: new Error('The response could not be read.'), retrying: inFlight };
      const record = answer.result === 'ok' ? answer.value : answer.degradedValue;
      if (record === null) return { kind: 'not-found', refreshing: inFlight };
      return { kind: 'loaded', record, incomplete: answer.result === 'degraded', refreshing: inFlight };
    }
  }
};

/**
 * One record, read by its id, for a details pane.
 *
 * The id is fixed for the model's lifetime, so a pane showing another record needs
 * another model. Reusing one would show the previous record, with its actions live
 * and bound to the previous id, while the next one loads.
 *
 * NOTE: a failed request is caught in `fetch` and held as an answer. Thrown, it would
 * become MobxQuery's `error`, which keeps the last good answer as `previous`, and a
 * retry turns that into `stale` WITH the old record: the pane would show it, actions
 * live, while the retry is still in flight. Held as an answer, the failure is what a
 * retry is stale on, so the pane stays failed until the new answer lands.
 */
export class RecordDetailsModel<T> {
  private readonly _query: MobxQuery<number, RecordRead<T>>;

  constructor(id: number, fetch: (id: number) => Promise<Received<T | null>>) {
    this._query = new MobxQuery<number, RecordRead<T>>({
      params: () => id,
      fetch: (recordId) => fetch(recordId).catch((thrown: unknown): RecordRead<T> => ({ result: 'failed', error: toError(thrown) })),
      debounceMs: 0,
    });
    makeObservable(this, { state: computed });
  }

  get state(): DetailsState<T> {
    return toDetailsState(this._query.value);
  }

  /** Read the record again: after an action changed or deleted it, or to retry a failure. */
  refresh(): void {
    void this._query.refresh();
  }
}
